import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ProposalCommandPalette from '../search/ProposalCommandPalette';
import { db } from '../../db/database';
import { pushWorkspaceBackup, reconcileLocalWorkspaceWithCloud } from '../../services/cloudBackup';
import * as authService from '../../services/authService';

const EXISTING_SESSION_RECONCILE_TIMEOUT_MS = 6000;

const sectionLabels = {
  '/': 'Dashboard',
  '/proposals': 'Library',
  '/library': 'Snippets',
  '/settings': 'Settings',
};

export default function Layout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [proposalSearchOpen, setProposalSearchOpen] = useState(false);
  const [workspaceReconcilePending, setWorkspaceReconcilePending] = useState(() => {
    const hasSession = !!authService.getActiveSessionToken();
    return hasSession && !authService.hasCurrentSessionBeenReconciled();
  });
  const location = useLocation();

  const currentSection = sectionLabels[location.pathname] || (location.pathname.startsWith('/proposals/') ? 'Editor' : '');

  // A restored browser session does not pass through login(), so give it the
  // same protected reconciliation gate before any workspace UI is interactive.
  // Fresh logins that already reconciled are skipped via the in-memory marker.
  useEffect(() => {
    const token = authService.getActiveSessionToken();
    if (!token || authService.hasCurrentSessionBeenReconciled()) {
      setWorkspaceReconcilePending(false);
      return undefined;
    }

    let cancelled = false;
    setWorkspaceReconcilePending(true);

    const reconcileExistingSession = async () => {
      try {
        const settings = await db.settings.get('global');
        if (settings?.googleBackupConnected && settings?.googleBackupUrl) {
          await reconcileLocalWorkspaceWithCloud(settings, { timeoutMs: EXISTING_SESSION_RECONCILE_TIMEOUT_MS });

          // Match a fresh login: once the protected cloud copy has been read,
          // start a non-blocking backup so offline/local-only edits and pending
          // deletion tombstones are carried upstream automatically.
          void pushWorkspaceBackup(settings).catch((syncErr) => {
            console.warn('ScopeShield: Restored-session backup was deferred; local workspace preserved.', syncErr);
          });
        }
        authService.markCurrentSessionReconciled();
      } catch (err) {
        // A transient cloud failure must not invalidate a valid session or
        // prevent local work. Leave the marker unset so a later remount can retry.
        console.warn('ScopeShield: Existing-session workspace reconciliation was unavailable; continuing locally.', err);
      } finally {
        if (!cancelled) setWorkspaceReconcilePending(false);
      }
    };

    void reconcileExistingSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close mobile nav on route change
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Close mobile nav when window grows to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1280) setMobileNavOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        const dialogs = document.querySelectorAll('[role="dialog"][aria-modal="true"]');
        const isPaletteOpen = Array.from(dialogs).some((d) => d.id === 'proposal-command-palette');
        
        if (dialogs.length > 0 && !isPaletteOpen) return;

        e.preventDefault();
        e.stopPropagation();
        setProposalSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Custom event listener for opening search
  useEffect(() => {
    const handleCustomOpen = () => setProposalSearchOpen(true);
    window.addEventListener('scopeshield:open-proposal-search', handleCustomOpen);
    return () => window.removeEventListener('scopeshield:open-proposal-search', handleCustomOpen);
  }, []);

  if (workspaceReconcilePending) {
    return (
      <div className="min-h-[100dvh] bg-slate-900 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[28px] p-8 sm:p-10 text-center shadow-2xl">
          <div className="w-16 h-16 bg-indigo-500/20 border border-indigo-500/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiIcons.FiRefreshCw} className="text-2xl text-indigo-300 animate-spin" />
          </div>
          <h2 className="text-white text-xl font-black tracking-tight mb-2">Syncing your workspace</h2>
          <p className="text-indigo-200 text-sm font-medium leading-relaxed">
            Checking the latest protected cloud copy before opening ScopeShield.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] bg-gray-50">
      <Sidebar 
        mobileOpen={mobileNavOpen} 
        onMobileClose={() => setMobileNavOpen(false)} 
        onOpenProposalSearch={() => setProposalSearchOpen(true)}
      />

      {/* Mobile / Tablet top bar — hidden on xl+ */}
      <header className="xl:hidden fixed top-0 left-0 right-0 z-30 bg-slate-900 text-white h-14 flex items-center justify-between px-4 shadow-lg">
        <div className="flex items-center gap-3 min-w-0">
          <button 
            onClick={() => setMobileNavOpen(true)} 
            className="flex items-center justify-center text-slate-300 hover:text-white rounded-lg active:bg-slate-800 shrink-0" 
            style={{ minWidth: '44px', minHeight: '44px' }} 
            aria-label="Open navigation"
          >
            <SafeIcon icon={FiIcons.FiMenu} className="text-xl" />
          </button>
          <div className="flex items-center gap-2 font-bold text-lg whitespace-nowrap">
            <span className="text-indigo-400">🛡️</span> ScopeShield
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button 
            onClick={() => setProposalSearchOpen(true)} 
            className="flex items-center justify-center text-slate-300 hover:text-white rounded-lg active:bg-slate-800 shrink-0" 
            style={{ minWidth: '44px', minHeight: '44px' }}
            aria-label="Search proposals"
          >
            <SafeIcon icon={FiIcons.FiSearch} className="text-xl" />
          </button>
          {currentSection && (
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest shrink-0 hidden sm:block">
              {currentSection}
            </span>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 min-w-0 w-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto pt-14 xl:pt-0">
          <Outlet />
        </div>
      </main>

      <ProposalCommandPalette 
        open={proposalSearchOpen} 
        onClose={() => setProposalSearchOpen(false)} 
      />
    </div>
  );
}